import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaXTwitter } from "react-icons/fa6";
import { FaDiscord, FaGithub, FaLinkedinIn, FaMedium } from "react-icons/fa";
import { SiGitbook } from "react-icons/si";
import { useProtocolMetrics } from '../hooks/useDuneData';
import MetricSkeleton from './MetricSkeleton';
import RealTimeIndicator from './RealTimeIndicator';

const iconSize = 24;

// react-icons has no Dune mark, so this is a monochrome take on it: a ring with its lower half filled
// by a wave, drawn in currentColor so it follows the hover color like the other icons.
const DuneIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M2.6 13.5C6 13.5 9 10 14 10s6.5 2 7.4 3.5A10 10 0 0 1 2.6 13.5Z" fill="currentColor" />
  </svg>
);

const SOCIAL_ICONS = {
  X: FaXTwitter,
  LinkedIn: FaLinkedinIn,
  Discord: FaDiscord,
  GitHub: FaGithub,
  Dune: DuneIcon,
  Medium: FaMedium,
};

/**
 * Enhanced ProductCard with dynamic metrics integration
 */
const ProductCard = ({
  title,
  logo,
  descriptionKey,
  metricsKey,
  socialLinks,
  projectUrl,
  docsUrl,
  protocolKey
}) => {
  const { t } = useTranslation();
  const dynamicMetrics = useProtocolMetrics(protocolKey);
  const handleCardClick = (e) => {
    // Don't open URL if clicking on social links
    if (e.target.closest('.social-links')) {
      return;
    }
    if (projectUrl) {
      window.open(projectUrl, '_blank');
    }
  };

  /**
   * Generates metrics array combining dynamic and static data
   */
  const generateMetrics = () => {
    const metrics = [];

    // Add dynamic TVL metric (priority 1)
    if (dynamicMetrics?.tvl) {
      metrics.push({
        id: 'tvl',
        content: dynamicMetrics.tvl.isLoading ? (
          <div className="flex items-center">
            <span className="text-main mr-2">✓</span>
            <MetricSkeleton />
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-main mr-2">✓</span>
            <span className="text-white">{dynamicMetrics.tvl.formatted}</span>
            <span className="ml-1 text-sm text-main-gray">{protocolKey === 'soroswap' ? t('products.totalVolume') : t('products.tvl')}</span>
            <RealTimeIndicator isRealTime={dynamicMetrics.tvl.isRealTime} />
          </div>
        ),
        isDynamic: true
      });
    }

    // Add dynamic accounts metric (priority 2)
    if (dynamicMetrics?.accounts) {
      metrics.push({
        id: 'accounts',
        content: dynamicMetrics.accounts.isLoading ? (
          <div className="flex items-center">
            <span className="text-main mr-2">✓</span>
            <MetricSkeleton />
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-main mr-2">✓</span>
            <span className="text-white">{dynamicMetrics.accounts.formatted}</span>
            <RealTimeIndicator isRealTime={dynamicMetrics.accounts.isRealTime} />
          </div>
        ),
        isDynamic: true
      });
    }

    // Add static metrics (filtered to avoid duplicates)
    // i18next returns "" for an undefined key and the key itself when it
    // doesn't resolve — neither is an array, so guard against both instead
    // of assuming translation always yields the expected shape.
    const rawMetrics = t(metricsKey, { returnObjects: true });
    const staticMetrics = Array.isArray(rawMetrics) ? rawMetrics : [];

    staticMetrics.forEach((metric, index) => {
      // Skip static TVL and accounts metrics if we have dynamic ones
      const isReplaceable = metric.toLowerCase().includes('tvl') || 
                           metric.toLowerCase().includes('accounts') || 
                           metric.toLowerCase().includes('users');
      
      if (!isReplaceable) {
        metrics.push({
          id: `static-${index}`,
          content: (
            <div className="flex items-start text-sm">
              <span className="text-main mr-2">✓</span>
              <span className="text-white">{metric}</span>
            </div>
          ),
          isDynamic: false
        });
      }
    });

    return metrics;
  };

  const allMetrics = generateMetrics();

  return (
    <div 
      style={{zIndex:99}}
      className="landingCard p-6 h-full flex flex-col cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Header */}
        <div className="mb-6 flex flex-col items-center">
          <img 
            src={logo} 
            alt={`${title} logo`} 
            className="h-20 my-8 object-contain" 
          />
          <p className="text-main-gray text-sm leading-relaxed text-center">{t(descriptionKey)}</p>
        </div>
        
        {/* Dynamic and Static Metrics */}
      <div className="flex-grow mb-6">
        <div className="space-y-3">
          {allMetrics.map((metric) => (
            <div key={metric.id} className="flex items-start text-sm">
              {metric.content}
            </div>
          ))}
        </div>
      </div>
      
      {/* Social Links and Docs */}
      <div className="pt-4 border-t border-white/10 social-links">
        <div className="flex justify-between items-start">
          <div>
            <span className="text-main-gray text-sm">{t('products.docs')}</span>
            <div className="flex gap-3 mt-2 ms-6">
              <a
                href={docsUrl}
                className="text-white hover:text-main transition"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SiGitbook size={iconSize}/>
              </a>
            </div>
          </div>
          <div className="text-right">
            <span className="text-main-gray text-sm me-8">{t('products.followUs')}</span>
            <div className="flex justify-end gap-3 mt-2 me-6">
              {socialLinks.map((link) => {
                const Icon = SOCIAL_ICONS[link.platform];
                return (
                  <a
                    key={link.platform}
                    href={link.url}
                    aria-label={`${title} ${link.platform}`}
                    className="text-white hover:text-main transition"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon size={iconSize} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ProductCard;